<!-- NotificationCenter.svelte — header bell + dropdown panel.
     Surfaces persistent equip warnings + system events from the
     `$domain/notifications` store. Renders an unread badge and a
     scrollable list grouped by recency. -->
<script lang="ts">
  import {
    notifications,
    unreadCount,
    markAllRead,
    clearAll,
    type NotifLevel,
  } from '$domain/notifications.svelte';
  import { SvelteSet } from 'svelte/reactivity';

  let open = $state(false);
  let panelEl = $state<HTMLDivElement | undefined>();
  let triggerEl = $state<HTMLButtonElement | undefined>();
  // Per-entry expanded set for grouped notifications. Reactive because
  // we mutate via reassignment (Svelte 5 runes require copy-on-write).
  let expanded = $state(new SvelteSet<string>());

  function toggleGroup(id: string): void {
    const next = new SvelteSet(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    expanded = next;
  }

  // Expose a global opener so other surfaces (toast "see in 🔔" actions,
  // keyboard shortcuts, etc.) can pop the panel without a tight import.
  $effect(() => {
    const w = window as unknown as { __cryptarchOpenNotifications?: () => void };
    w.__cryptarchOpenNotifications = () => {
      open = true;
      // Defer focus until panel is rendered.
      setTimeout(() => panelEl?.focus(), 0);
    };
    return () => {
      if (w.__cryptarchOpenNotifications) delete w.__cryptarchOpenNotifications;
    };
  });

  const log = $derived(notifications());
  const unread = $derived(unreadCount());

  // Markup `Read` once the panel is open (matches the user's mental model:
  // looking IS reading). We delay one frame so the badge doesn't flash off
  // before the user notices it.
  $effect(() => {
    if (!open) return;
    const t = setTimeout(() => markAllRead(), 300);
    return () => clearTimeout(t);
  });

  function toggle(): void {
    open = !open;
  }

  function onDocClick(e: MouseEvent): void {
    if (!open) return;
    const t = e.target as Node | null;
    if (!t) return;
    if (panelEl?.contains(t)) return;
    if (triggerEl?.contains(t)) return;
    open = false;
  }
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && open) {
      open = false;
      triggerEl?.focus();
    }
  }

  function relTime(ms: number): string {
    const diff = Date.now() - ms;
    const m = Math.round(diff / 60_000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(ms).toISOString().slice(0, 10);
  }

  function levelGlyph(l: NotifLevel): string {
    return l === 'error' ? '✕' : l === 'warn' ? '!' : 'i';
  }
</script>

<svelte:window onclick={onDocClick} onkeydown={onKey} />

<div class="notif-wrap">
  <button
    bind:this={triggerEl}
    type="button"
    class="bell"
    class:has-unread={unread > 0}
    aria-label={unread > 0 ? `notifications, ${unread} unread` : 'notifications'}
    aria-expanded={open}
    aria-haspopup="dialog"
    onclick={toggle}
    title={unread > 0 ? `${unread} unread notification${unread === 1 ? '' : 's'}` : 'notifications'}
  >
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
      <path
        d="M8 1.5c-.6 0-1.1.5-1.1 1.1v.4c-1.8.5-3.1 2.1-3.1 4v2.4l-1.1 1.6h10.6L12.2 9.4V7c0-1.9-1.3-3.5-3.1-4v-.4c0-.6-.5-1.1-1.1-1.1zm-1.6 11.6c.1.8.8 1.4 1.6 1.4s1.5-.6 1.6-1.4z"
        fill="currentColor"
      />
    </svg>
    {#if unread > 0}
      <span class="badge">{unread > 99 ? '99+' : unread}</span>
    {/if}
  </button>

  {#if open}
    <div bind:this={panelEl} class="panel" role="dialog" aria-label="notifications">
      <header>
        <strong>Notifications</strong>
        {#if log.entries.length > 0}
          <button type="button" class="link" onclick={clearAll}>clear all</button>
        {/if}
      </header>
      {#if log.entries.length === 0}
        <p class="empty">
          No notifications yet. Equip warnings, rate-limit nudges, and other persistent events will
          appear here.
        </p>
      {:else}
        <ul class="list">
          {#each log.entries as n (n.id)}
            <li class="entry level-{n.level}" class:fresh={n.at > log.lastReadAt}>
              <span class="glyph" aria-hidden="true">{levelGlyph(n.level)}</span>
              <div class="meta">
                <div class="title">{n.title}</div>
                <div class="body">{n.body}</div>
                {#if n.kind === 'group' && n.groupItems && n.groupItems.length > 0}
                  <button
                    type="button"
                    class="group-toggle"
                    aria-expanded={expanded.has(n.id)}
                    onclick={() => toggleGroup(n.id)}
                  >
                    {expanded.has(n.id) ? '▾ hide' : '▸ show'}
                    {n.groupItems.length} item{n.groupItems.length === 1 ? '' : 's'}
                  </button>
                  {#if expanded.has(n.id)}
                    <ul class="group-items">
                      {#each n.groupItems as g, i (i)}
                        <li>
                          <span class="g-title">{g.title}</span>
                          {#if g.body}<span class="g-body"> — {g.body}</span>{/if}
                        </li>
                      {/each}
                    </ul>
                  {/if}
                {/if}
                <div class="foot">
                  <span class="time">{relTime(n.at)}</span>
                  {#if n.source}<span class="source">· {n.source}</span>{/if}
                </div>
              </div>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</div>

<style>
  .notif-wrap {
    position: relative;
    display: inline-flex;
  }
  .bell {
    appearance: none;
    background: transparent;
    border: 1px solid transparent;
    color: var(--fg-muted);
    padding: 5px 8px;
    border-radius: var(--radius-2);
    cursor: pointer;
    position: relative;
    display: inline-flex;
    align-items: center;
    transition:
      color var(--speed-fast) var(--easing),
      border-color var(--speed-fast) var(--easing),
      background var(--speed-fast) var(--easing);
  }
  .bell:hover {
    color: var(--fg);
    background: var(--bg-elev-2);
  }
  .bell.has-unread {
    color: var(--fg);
  }
  .bell:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  .badge {
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 16px;
    height: 16px;
    padding: 0 4px;
    background: var(--danger);
    color: white;
    font-family: var(--font-mono);
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
  .panel {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: min(360px, 90vw);
    max-height: 70vh;
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-3);
    box-shadow: 0 12px 32px -8px rgba(0, 0, 0, 0.5);
    display: flex;
    flex-direction: column;
    z-index: 50;
  }
  .panel header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--border);
  }
  .panel header strong {
    font-size: var(--text-sm);
    color: var(--fg-strong);
  }
  .panel .link {
    appearance: none;
    background: transparent;
    border: none;
    color: var(--fg-muted);
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
    padding: 2px 4px;
  }
  .panel .link:hover {
    color: var(--accent);
  }
  .empty {
    margin: 0;
    padding: var(--space-4);
    font-size: 12px;
    color: var(--fg-muted);
    line-height: 1.5;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    flex: 1;
  }
  .entry {
    display: flex;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid color-mix(in srgb, var(--border) 50%, transparent);
    position: relative;
  }
  .entry:last-child {
    border-bottom: none;
  }
  .entry.fresh::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: var(--accent);
  }
  .glyph {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--bg-elev-3);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .level-warn .glyph {
    background: color-mix(in srgb, var(--warn) 25%, var(--bg-elev-3));
    color: var(--warn);
  }
  .level-error .glyph {
    background: color-mix(in srgb, var(--danger) 25%, var(--bg-elev-3));
    color: var(--danger);
  }
  .meta {
    flex: 1;
    min-width: 0;
  }
  .title {
    font-size: 12px;
    font-weight: 600;
    color: var(--fg-strong);
    margin-bottom: 2px;
    overflow-wrap: anywhere;
  }
  .body {
    font-size: 11px;
    line-height: 1.45;
    color: var(--fg-muted);
    margin-bottom: 4px;
    overflow-wrap: anywhere;
  }
  .foot {
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--fg-subtle);
    letter-spacing: 0.04em;
  }
  .source {
    margin-left: 2px;
    text-transform: lowercase;
  }
  .group-toggle {
    appearance: none;
    background: transparent;
    border: 0;
    color: var(--accent);
    font: inherit;
    font-size: 11px;
    font-family: var(--font-mono);
    letter-spacing: 0.04em;
    padding: 2px 0;
    margin-bottom: 4px;
    cursor: pointer;
    align-self: flex-start;
  }
  .group-toggle:hover {
    color: var(--fg);
  }
  .group-toggle:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 2px;
  }
  .group-items {
    list-style: none;
    margin: 0 0 6px;
    padding: 6px 8px;
    background: var(--bg-elev-1);
    border-left: 2px solid var(--border);
    border-radius: 0 var(--radius-2) var(--radius-2) 0;
    font-size: 11px;
    line-height: 1.5;
    color: var(--fg-muted);
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .group-items .g-title {
    color: var(--fg);
    font-weight: 500;
  }
  .group-items .g-body {
    color: var(--fg-subtle);
  }
</style>
